import React, { useState } from 'react';
import { BookOpen, Calendar, User, Eye, MessageSquare } from 'lucide-react';
interface BlogPost {
  id: string;
  title: string;
  date: string;
  author: string;
  views: number;
  excerpt: string;
  content: string;
}
const blogPosts: BlogPost[] = [{
  id: '1',
  title: 'My fingerprints do not change; Why do I need a new fingerprint card?',
  date: 'December 29th, 2025 at 8:59 am',
  author: 'JON KICHEN',
  views: 27,
  excerpt: 'The real estate industry in Arizona is in a bit of an uproar over the "new" requirement from ADRE that licensees must obtain a new fingerprint clearance card when they renew their license.',
  content: `The real estate industry in Arizona is in a bit of an uproar over the "new" requirement from ADRE that licensees must obtain a new fingerprint clearance card when they renew their license. Many have stated, "My fingerprints do not change, so why do I need a new fingerprint clearance card?"

Here is why:

The requirement for a fingerprint clearance card began in the early 1990s. At that time, license applicants went to a facility to have their fingerprints placed on a cardboard card, which they included in their application to ADRE. Assuming all other requirements were fulfilled, ADRE would approve the application. That applicant, now a licensee, would be on the street dealing with clients.

Monthly, ADRE would receive several hundred applications and fingerprint cards. ADRE would bundle the cards and send them to DPS (Department of Public Safety). Once DPS completed their research, they would report the results to ADRE. Of a bundle of 400 applicants, DPS might report that 8 people had a felony—in Arizona, out of state, or at some point in the past.

ADRE would then decide whether to ignore the felony or take action against that licensee who had been active for several months. Almost every month, ADRE would either revoke a license that was issued months earlier or impose sanctions against that licensee.

By the early 2000s, ADRE realized that every month they were approving licenses for people with significant felonies in their background.

ADRE took the position that the process needed to change: applicants should get approval of their fingerprint search BEFORE applying for a real estate license. Within a few years, the process did change.

State statute and DPS rules clearly state that fingerprint approvals are good for 6 years. As a result, once an applicant receives approval from DPS and obtains their real estate license, ADRE could require a new clearance card when the licensee renews.

Yet, until now, ADRE did not require a new clearance card. It was either a conscious decision or an oversight by the various Commissioners who preceded current Commissioner Susan Nicolson.

In 2025, ADRE received notification from DPS that some licensees had recent felony convictions yet were able to renew their license. Several included assault, child endangerment, and other felonies where someone was hurt. While rule and statute require licensees to "self-report" any felony conviction or plea bargain, it became clear that these licensees failed to do so.

While your fingerprints typically do not change, your background could change. Commissioner Nicolson realized that fingerprint clearance cards expire after 6 years. As a result, and well within her regulatory authority, she is now enforcing a statute/rule that has been in place for decades.

Beginning January 2027, ADRE will require a new fingerprint clearance card for any licensee at renewal. Licensees must repeat the process: get new fingerprints taken, submit them to DPS, and wait for DPS approval. If your license expires in the coming months and your original clearance was obtained more than 6 years ago, ADRE will renew your license for now.

If you are unsure whether your fingerprint clearance is valid, simply log in to your licensee profile at ADRE. The opening page will indicate whether ADRE has a valid clearance on file.

We recommend not waiting too long, as thousands of agents will need to complete this process. Many companies are starting to offer events where licensees can obtain their fingerprint card to submit to DPS.

DPS charges $67 to process the application, and you will need to find an entity to take your fingerprints and submit the application.

ADRE should have every tool available to deny or revoke a license for a licensee who has committed a serious crime.`
}, {
  id: '2',
  title: 'THE CRIMINALS ARE HERE! (And they are calling/texting & emailing you)',
  date: 'October 30th, 2025 at 7:35 pm',
  author: 'JON KICHEN',
  views: 45,
  excerpt: 'Learn about the latest scams targeting real estate professionals and how to protect yourself.',
  content: 'Full content for this blog post...'
}, {
  id: '3',
  title: 'Everything in perspective. October 11, 2025',
  date: 'October 13th, 2025 at 9:08 am',
  author: 'JON KICHEN',
  views: 32,
  excerpt: 'A look at the current state of the Arizona real estate market.',
  content: 'Full content for this blog post...'
}, {
  id: '4',
  title: 'What About the Animals?',
  date: 'July 21st, 2025 at 11:57 am',
  author: 'JON KICHEN',
  views: 28,
  excerpt: 'Important considerations when dealing with properties that have pets.',
  content: 'Full content for this blog post...'
}, {
  id: '5',
  title: 'Choice of Forms?',
  date: 'June 18th, 2025 at 2:57 pm',
  author: 'JON KICHEN',
  views: 41,
  excerpt: 'Understanding which real estate forms to use in different situations.',
  content: 'Full content for this blog post...'
}, {
  id: '6',
  title: "Deed Fraud Can't Happen to You! Right?",
  date: 'April 23rd, 2025 at 1:15 pm',
  author: 'JON KICHEN',
  views: 53,
  excerpt: 'Protecting yourself and your clients from deed fraud.',
  content: 'Full content for this blog post...'
}];
export function Blog() {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  return <div className="min-h-screen bg-white pt-40 lg:pt-52 pb-20" data-id="element-632">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-633">
        {/* Page Header */}
        <div className="mb-12 text-center" data-id="element-634">
          <div className="flex items-center justify-center mb-4" data-id="element-635">
            <BookOpen className="w-12 h-12 text-brand-purple-600 mr-4" data-id="element-636" />
            <h1 className="text-4xl font-bold text-gray-900" data-id="element-637">
              Desert Sage Seminars Blog
            </h1>
          </div>
          <p className="text-lg text-gray-600" data-id="element-638">
            Industry insights, updates, and news for Arizona real estate
            professionals
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-3 gap-8" data-id="element-639">
          {/* Main Content - Blog Posts */}
          <div className="lg:col-span-2" data-id="element-640">
            {!selectedPost ? <div className="space-y-8" data-id="element-641">
                {blogPosts.map(post => <article key={post.id} className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden hover:border-brand-purple-300 transition-colors" data-id="element-642">
                    <div className="p-8" data-id="element-643">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 hover:text-brand-purple-600 transition-colors" data-id="element-644">
                        <button onClick={() => setSelectedPost(post)} className="text-left" data-id="element-645">
                          {post.title}
                        </button>
                      </h2>

                      {/* Post Meta */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4" data-id="element-646">
                        <div className="flex items-center" data-id="element-647">
                          <Calendar className="w-4 h-4 mr-1" data-id="element-648" />
                          {post.date}
                        </div>
                        <div className="flex items-center" data-id="element-649">
                          <User className="w-4 h-4 mr-1" data-id="element-650" />
                          {post.author}
                        </div>
                        <div className="flex items-center" data-id="element-651">
                          <Eye className="w-4 h-4 mr-1" data-id="element-652" />
                          {post.views} views
                        </div>
                      </div>

                      {/* Excerpt */}
                      <p className="text-base text-gray-700 leading-relaxed mb-4" data-id="element-653">
                        {post.excerpt}
                      </p>

                      {/* Read More Button */}
                      <button onClick={() => setSelectedPost(post)} className="text-brand-purple-600 hover:text-brand-purple-700 font-semibold" data-id="element-654">
                        Read More →
                      </button>
                    </div>
                  </article>)}
              </div> /* Full Blog Post View */ : <div data-id="element-655">
                <button onClick={() => setSelectedPost(null)} className="text-brand-purple-600 hover:text-brand-purple-700 font-semibold mb-6 flex items-center" data-id="element-656">
                  ← Back to Blog
                </button>

                <article className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden" data-id="element-657">
                  <div className="p-8" data-id="element-658">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-659">
                      {selectedPost.title}
                    </h1>

                    {/* Post Meta */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-8 pb-6 border-b border-gray-200" data-id="element-660">
                      <div className="flex items-center" data-id="element-661">
                        <Calendar className="w-4 h-4 mr-1" data-id="element-662" />
                        {selectedPost.date}
                      </div>
                      <div className="flex items-center" data-id="element-663">
                        <User className="w-4 h-4 mr-1" data-id="element-664" />
                        Posted by {selectedPost.author}
                      </div>
                      <div className="flex items-center" data-id="element-665">
                        <Eye className="w-4 h-4 mr-1" data-id="element-666" />
                        Views: {selectedPost.views}
                      </div>
                    </div>

                    {/* Full Content */}
                    <div className="prose prose-lg max-w-none" data-id="element-667">
                      <div className="text-base leading-relaxed text-gray-700 whitespace-pre-line" data-id="element-668">
                        {selectedPost.content}
                      </div>
                    </div>

                    {/* Comments Section */}
                    <div className="mt-12 pt-8 border-t border-gray-200" data-id="element-669">
                      <div className="flex items-center mb-6" data-id="element-670">
                        <MessageSquare className="w-6 h-6 text-brand-purple-600 mr-2" data-id="element-671" />
                        <h3 className="text-xl font-bold text-gray-900" data-id="element-672">
                          Add Your Comment
                        </h3>
                      </div>

                      <form className="space-y-4" data-id="element-673">
                        <div data-id="element-674">
                          <label htmlFor="comment-name" className="block text-sm font-medium text-gray-900 mb-2" data-id="element-675">
                            Name<span className="text-red-500" data-id="element-676">*</span>
                          </label>
                          <input type="text" id="comment-name" className="block w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500 text-base" data-id="element-677" />
                        </div>

                        <div data-id="element-678">
                          <label htmlFor="comment-email" className="block text-sm font-medium text-gray-900 mb-2" data-id="element-679">
                            Email<span className="text-red-500" data-id="element-680">*</span>
                          </label>
                          <input type="email" id="comment-email" className="block w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500 text-base" data-id="element-681" />
                        </div>

                        <div data-id="element-682">
                          <label htmlFor="comment-text" className="block text-sm font-medium text-gray-900 mb-2" data-id="element-683">
                            Comment<span className="text-red-500" data-id="element-684">*</span>
                          </label>
                          <textarea id="comment-text" rows={6} className="block w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500 text-base resize-none" data-id="element-685" />
                        </div>

                        <button type="submit" className="px-6 py-3 bg-brand-purple-600 text-white font-bold rounded-md hover:bg-brand-purple-700 transition-colors" data-id="element-686">
                          Submit Comment
                        </button>
                      </form>
                    </div>
                  </div>
                </article>
              </div>}
          </div>

          {/* Sidebar - Blog Articles List */}
          <div className="lg:col-span-1" data-id="element-687">
            <div className="bg-white rounded-lg border-2 border-[#fcb53d] overflow-hidden sticky top-40" data-id="element-688">
              <div className="bg-white p-6 border-b-2 border-[#fcb53d]" data-id="element-689">
                <h2 className="text-2xl font-bold text-[#fcb53d]" data-id="element-690">
                  Blog Articles
                </h2>
              </div>

              <div className="p-4" data-id="element-691">
                <div className="space-y-6" data-id="element-692">
                  {blogPosts.map(post => <div key={post.id} className="pb-6 border-b border-gray-200 last:border-b-0" data-id="element-693">
                      <button onClick={() => setSelectedPost(post)} className="text-left w-full group" data-id="element-694">
                        <h3 className="text-base font-bold text-brand-purple-600 hover:text-brand-purple-700 mb-2 transition-colors" data-id="element-695">
                          {post.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-1" data-id="element-696">
                          {post.date}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-697">
                          Posted by {post.author}
                        </p>
                      </button>
                    </div>)}
                </div>
              </div>
            </div>

            {/* CTA Below Sidebar */}
            <div className="mt-8 bg-brand-purple-50 rounded-lg p-6 border border-brand-purple-100" data-id="element-698">
              <h3 className="text-lg font-bold text-brand-purple-900 mb-3" data-id="element-699">
                Stay Updated
              </h3>
              <p className="text-sm text-gray-700 mb-4" data-id="element-700">
                Get the latest news and updates about Arizona real estate
                regulations, continuing education, and industry insights.
              </p>
              <a href="/Contact" className="inline-flex items-center px-4 py-2 bg-brand-purple-600 text-white font-bold rounded-md hover:bg-brand-purple-700 transition-colors text-sm" data-id="element-701">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>;
}